import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  onSnapshot,
  getDoc,
  getDocs,
  serverTimestamp,
  writeBatch,
  FieldValue
} from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { db, auth } from '@/js/firebase'
import { logger } from '@/services/loggerService'
import { PROJECT_STATUS } from '@/utils/projectUtils'
import { useAdminProjectsStore } from '@/stores/adminProjectsStore'
import { deliverableService } from '@/services/deliverableService'
import { valuesEqual } from '@/utils/valueCompare'

// projectId → primary episode deliverable id. Only hits are cached: a miss may
// just mean the template hasn't created the deliverable yet.
const primaryDeliverableIds = new Map()

// Bookkeeping keys that ride along with real edits and would only add noise
// to the activity log as plain field diffs.
const UNLOGGED_EPISODE_FIELDS = new Set([
  'episodeContentPrevious',
  'episodeContentStale',
  'episodeMetaConfirmedAt',
  'episodeMetaConfirmedBy'
])

/**
 * Resolve an episode project's primary deliverable (where its activity log lives).
 *
 * Client users may only query deliverables when the query is constrained to one
 * of their companies (firestore.rules: `resource.data.companyId in
 * request.auth.token.companyIds`), so pass the project's company whenever it's
 * known. The unscoped retry covers admins on legacy docs whose companyId drifted;
 * for a client it is simply denied and swallowed.
 *
 * @param {string} projectId
 * @param {string|null} companyId
 * @returns {Promise<string|null>}
 */
export async function getPrimaryEpisodeDeliverableId(projectId, companyId = null) {
  if (!projectId) return null
  if (primaryDeliverableIds.has(projectId)) return primaryDeliverableIds.get(projectId)

  const find = async (...constraints) => {
    const snap = await getDocs(query(
      collection(db, 'deliverables'),
      where('projectId', '==', projectId),
      ...constraints
    ))
    return snap.docs.find(d => d.data().isPrimaryEpisodeDeliverable === true)?.id || null
  }

  let id = null
  if (companyId) id = await find(where('companyId', '==', companyId)).catch(() => null)
  if (!id) id = await find().catch(() => null)
  if (id) primaryDeliverableIds.set(projectId, id)
  return id
}

/**
 * Write activity-log entries for an episode onto its primary deliverable.
 * Never throws — logging must not fail the caller's save.
 *
 * @param {string} projectId
 * @param {string|null} companyId
 * @param {Array<{action: string, field?: string, oldValue?: *, newValue?: *, note?: string}>} entries
 */
export async function logEpisodeActivity(projectId, companyId, entries) {
  if (!entries?.length) return
  try {
    const deliverableId = await getPrimaryEpisodeDeliverableId(projectId, companyId)
    if (!deliverableId) return
    await Promise.all(entries.map(e => deliverableService.logActivity(
      deliverableId, e.action, e.field || null, e.oldValue ?? null, e.newValue ?? null, e.note || null
    )))
  } catch (error) {
    logger.warn('[EpisodeService] Failed to log episode activity', { projectId, error: error.message })
  }
}

// Read a (possibly dotted, e.g. `publishScheduling.apple.scheduled`) field path.
function getPath(data, path) {
  return path.split('.').reduce((obj, key) => (obj == null ? undefined : obj[key]), data)
}

class EpisodeService {
  constructor() {
    this.projectsCollection = 'projects'
  }

  /**
   * One-shot fetch of a show's episodes, ordered by episodeNumber.
   * For flows that need the list once rather than a live subscription —
   * counting or deleting a show's episodes, where a listener would be wrong
   * (it would fire mid-delete as documents disappear).
   */
  async getShowEpisodes(showId) {
    const snapshot = await getDocs(query(
      collection(db, this.projectsCollection),
      where('showId', '==', showId)
    ))
    const episodes = []
    snapshot.forEach(docSnap => {
      episodes.push({ id: docSnap.id, ...docSnap.data() })
    })
    episodes.sort((a, b) => (a.episodeNumber || 0) - (b.episodeNumber || 0))
    logger.info('[EpisodeService] Fetched show episodes', { showId, count: episodes.length })
    return episodes
  }

  /**
   * Subscribe to episodes for a show (real-time, ordered by episodeNumber)
   */
  subscribeToShowEpisodes(showId, callback, companyId = null) {
    // Query episodes by showId, optionally filtering by company for client users
    // (Firestore rules require a company filter for non-admin queries)
    const constraints = [where('showId', '==', showId)]
    if (companyId) {
      constraints.push(where('company', '==', companyId))
    }
    const q = query(
      collection(db, this.projectsCollection),
      ...constraints
    )
    return onSnapshot(
      q,
      (snapshot) => {
        const episodes = []
        snapshot.forEach(docSnap => {
          episodes.push({ id: docSnap.id, ...docSnap.data() })
        })
        episodes.sort((a, b) => (a.episodeNumber || 0) - (b.episodeNumber || 0))
        logger.info('[EpisodeService] Episodes snapshot', { showId, count: episodes.length })
        callback(episodes)
      },
      (error) => {
        logger.error('[EpisodeService] Episodes snapshot error', { error: error.message, showId })
      }
    )
  }

  /**
   * Compute the next number an unpublished episode should occupy:
   * one greater than the highest published episodeNumber (or 1 if none).
   */
  getNextUnpublishedNumber(episodes) {
    const publishedNumbers = (episodes || [])
      .filter(e => e.published)
      .map(e => e.episodeNumber || 0)
    const maxPublished = publishedNumbers.length ? Math.max(...publishedNumbers) : 0
    return maxPublished + 1
  }

  /**
   * Create a new episode (project with type: 'episode')
   */
  async createEpisode(show, { name, description, episodeNumber, subscribers }) {
    try {
      const user = auth.currentUser
      if (!user) throw new Error('User not authenticated')

      const episodeData = {
        name: name.trim(),
        type: 'episode',
        showId: show.id,
        seasonId: null,
        episodeNumber,
        published: false,
        publishedAt: null,
        goLiveDate: null,
        company: show.companyId,
        companyName: show.companyName || '',
        contractId: show.contractId || null,
        status: PROJECT_STATUS.DRAFT,
        description: description?.trim() || '',
        subscribers: subscribers || [],
        episodeTitle: '',
        episodeDescription: '',
        episodeThumbnailUrl: '',
        episodeThumbnailPath: '',
        createdBy: user.uid,
        createdByName: user.displayName || user.email || 'Unknown',
        dateCreated: serverTimestamp(),
        dateUpdated: serverTimestamp(),
        dateSubmitted: serverTimestamp()
      }

      const docRef = await addDoc(collection(db, this.projectsCollection), episodeData)

      logger.info('[EpisodeService] Episode created', {
        episodeId: docRef.id,
        showId: show.id,
        episodeNumber
      })

      return { id: docRef.id, ...episodeData }
    } catch (error) {
      logger.error('[EpisodeService] Failed to create episode', { error: error.message })
      throw error
    }
  }

  /**
   * Delete an unbooked (Draft) episode. Nothing to cascade: sessions and
   * deliverables are only created once the episode is submitted.
   */
  async deleteDraftEpisode(episodeId) {
    try {
      await deleteDoc(doc(db, this.projectsCollection, episodeId))
      logger.info('[EpisodeService] Draft episode deleted', { episodeId })
    } catch (error) {
      logger.error('[EpisodeService] Failed to delete draft episode', {
        error: error.message,
        episodeId
      })
      throw error
    }
  }

  /**
   * Reorder episodes — batch update episodeNumber for all provided IDs
   * @param {Array<{id: string, episodeNumber: number}>} orderedEpisodes
   */
  async reorderEpisodes(orderedEpisodes) {
    try {
      const batch = writeBatch(db)

      for (const { id, episodeNumber } of orderedEpisodes) {
        const ref = doc(db, this.projectsCollection, id)
        batch.update(ref, { episodeNumber })
      }

      await batch.commit()

      logger.info('[EpisodeService] Episodes reordered', {
        count: orderedEpisodes.length
      })
    } catch (error) {
      logger.error('[EpisodeService] Failed to reorder episodes', { error: error.message })
      throw error
    }
  }

  /**
   * Update an episode.
   *
   * The single choke point for episode-field writes, so it is also where every
   * change is written to the primary deliverable's activity log.
   *
   * @param {string} episodeId
   * @param {Object} updates
   * @param {Object} [options]
   * @param {string} [options.note] - note attached to every log entry (e.g. "AI generated")
   * @param {string} [options.action] - extra entry to log once, for writes whose only
   *   changes are bookkeeping flags (e.g. `episode_content_kept`)
   */
  async updateEpisode(episodeId, updates, options = {}) {
    try {
      const ref = doc(db, this.projectsCollection, episodeId)
      // Old values for the activity-log diff. A failed read only costs the log entry.
      const before = await getDoc(ref).then(s => s.data() || null).catch(() => null)
      // episodeTitle is the public-facing title and is independent of the project `name`.
      const payload = { ...updates, dateUpdated: serverTimestamp() }
      await updateDoc(ref, payload)
      // This write bypasses adminProjectsStore, whose cached copy would otherwise
      // keep serving the old value and visibly revert the edit.
      useAdminProjectsStore().patchLocalProject(episodeId, updates)
      logger.info('[EpisodeService] Episode updated', { episodeId })
      // Fire-and-forget: the save has landed; logging must not delay the UI.
      if (before) {
        try {
          this._logEpisodeUpdate(episodeId, before, updates, options)
        } catch (logError) {
          logger.warn('[EpisodeService] Failed to log episode update', { episodeId, error: logError.message })
        }
      }
    } catch (error) {
      logger.error('[EpisodeService] Failed to update episode', { error: error.message, episodeId })
      throw error
    }
  }

  _logEpisodeUpdate(episodeId, before, updates, { note = null, action = null } = {}) {
    const entries = []
    if (action) entries.push({ action, note })
    if (updates.episodeMetaConfirmedAt) entries.push({ action: 'episode_meta_confirmed', note })
    for (const [field, newValue] of Object.entries(updates)) {
      if (UNLOGGED_EPISODE_FIELDS.has(field) || newValue instanceof FieldValue) continue
      const oldValue = getPath(before, field)
      if (valuesEqual(oldValue, newValue)) continue
      entries.push({ action: 'field_update', field, oldValue, newValue, note })
    }
    logEpisodeActivity(episodeId, before.company || before.companyId || null, entries)
  }

  /**
   * Set (or change) an episode's publish date.
   *
   * Goes through the `setEpisodePublishDate` callable rather than a direct write:
   * the 24h notice rules, the audit entry and the admin flag all have to be
   * decided on the server clock by a verified caller. Firestore rules deny a
   * client `goLiveDate` write, so this is the only path that works for them.
   *
   * @param {string} episodeId
   * @param {Date|null} date - null clears the date (admins only)
   * @returns {Promise<{goLiveDate: string|null, direction: string|null}>}
   * @throws {Error} with the server's own message, ready to show the user
   */
  async setPublishDate(episodeId, date) {
    try {
      const fn = httpsCallable(getFunctions(), 'setEpisodePublishDate')
      const { data } = await fn({
        projectId: episodeId,
        goLiveDate: date ? new Date(date).toISOString() : null
      })
      // Mirrors updateEpisode: the admin store's cached copy would otherwise keep
      // serving the old date and visibly revert the change.
      useAdminProjectsStore().patchLocalProject(episodeId, {
        goLiveDate: date ? new Date(date) : null
      })
      logger.info('[EpisodeService] Publish date set', { episodeId, direction: data?.direction })
      return data
    } catch (error) {
      // failed-precondition = the callable's publish-date policy said no (expected, not a bug)
      const log = error.code === 'functions/failed-precondition' ? logger.warn : logger.error
      log.call(logger, '[EpisodeService] Failed to set publish date', {
        error: error.message,
        episodeId
      })
      // Callable errors carry the product copy ("This change is within 24 hours…"),
      // so hand it straight to the caller instead of a generic failure.
      throw new Error(error.message || 'Failed to set the publish date')
    }
  }

  /**
   * Publish an episode — locks its episodeNumber and stamps publishedAt.
   * Throws if another published episode in `allEpisodes` already uses that number.
   */
  async publishEpisode(episodeId, episodeNumber, allEpisodes = []) {
    const conflict = allEpisodes.find(
      e => e.id !== episodeId && e.published && (e.episodeNumber || 0) === episodeNumber
    )
    if (conflict) {
      // Benign validation: another published episode already holds this number.
      // Surfaced to the user via toast — log at warn so it stays out of the error log.
      logger.warn('[EpisodeService] Publish blocked: episode number already in use', { episodeId, episodeNumber })
      throw new Error(`Episode #${episodeNumber} is already published.`)
    }

    try {
      const ref = doc(db, this.projectsCollection, episodeId)
      await updateDoc(ref, {
        published: true,
        publishedAt: serverTimestamp(),
        episodeNumber,
        dateUpdated: serverTimestamp()
      })
      logger.info('[EpisodeService] Episode published', { episodeId, episodeNumber })
    } catch (error) {
      logger.error('[EpisodeService] Failed to publish episode', { error: error.message, episodeId })
      throw error
    }
  }

  /**
   * Unpublish an episode — clears the publish flag and timestamp.
   * Caller should follow up with renormalizeUnpublished to rejoin the unpublished sequence.
   */
  async unpublishEpisode(episodeId) {
    try {
      const ref = doc(db, this.projectsCollection, episodeId)
      await updateDoc(ref, {
        published: false,
        publishedAt: null,
        dateUpdated: serverTimestamp()
      })
      logger.info('[EpisodeService] Episode unpublished', { episodeId })
    } catch (error) {
      logger.error('[EpisodeService] Failed to unpublish episode', { error: error.message, episodeId })
      throw error
    }
  }

  /**
   * Archive / restore an episode — the alternative end state to published.
   * Mutually exclusive with published. Caller renormalizes the unpublished sequence.
   */
  async setArchived(episodeId, archived) {
    try {
      const ref = doc(db, this.projectsCollection, episodeId)
      await updateDoc(ref, {
        archived,
        archivedAt: archived ? serverTimestamp() : null,
        ...(archived && { published: false, publishedAt: null }),
        dateUpdated: serverTimestamp()
      })
      logger.info('[EpisodeService] Episode archive state set', { episodeId, archived })
    } catch (error) {
      logger.error('[EpisodeService] Failed to set archive state', { error: error.message, episodeId })
      throw error
    }
  }

  /**
   * Renormalize unpublished episode numbers to a contiguous block starting at
   * max(publishedEpisodeNumber) + 1, in the visual order provided.
   * @param {Array} allEpisodes — full episode list (used to compute the start)
   * @param {Array<string>} orderedUnpublishedIds — unpublished episode IDs in their new visual order
   */
  async renormalizeUnpublished(allEpisodes, orderedUnpublishedIds) {
    try {
      const start = this.getNextUnpublishedNumber(allEpisodes)
      const batch = writeBatch(db)

      orderedUnpublishedIds.forEach((id, i) => {
        const ref = doc(db, this.projectsCollection, id)
        batch.update(ref, { episodeNumber: start + i })
      })

      await batch.commit()
      logger.info('[EpisodeService] Unpublished episodes renormalized', {
        count: orderedUnpublishedIds.length,
        start
      })
    } catch (error) {
      logger.error('[EpisodeService] Failed to renormalize unpublished', { error: error.message })
      throw error
    }
  }
}

export const episodeService = new EpisodeService()
